import config from "../config";
import { storage } from "../utils/storage";
import { enqueueRequest } from "./request-queue";

class Service {
  constructor() {
    // Keep a snapshot for callers that still read services.domain,
    // but always prefer the live getter below for actual requests.
    this._domainSnapshot = config.API_BASE_URL;
  }

  get domain() {
    return config.API_BASE_URL || this._domainSnapshot || "";
  }

  // Utility to combine URLs safely
  joinURL(baseURL, url) {
    return `${String(baseURL || "").replace(/\/$/, "")}/${String(url || "").replace(
      /^\//,
      "",
    )}`;
  }

  /**
   * @param {string} url
   * @param {string} method
   * @param {any} [data]
   * @param {{ priority?: number, skipQueue?: boolean }} [opts]
   */
  request(url, method = "POST", data = null, opts = {}) {
    const token = storage.getItem("access_token");
    const methodUpper = (method || "POST").toUpperCase();
    const hasBody = data != null && methodUpper !== "GET" && methodUpper !== "HEAD";

    // Only send Content-Type when there is a JSON body.
    // Putting Content-Type: application/json on GET forces a CORS preflight
    // (and Authorization already does) — avoid unnecessary OPTIONS when possible.
    const headers = {
      Accept: "application/json",
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const fullUrl = this.joinURL(this.domain, url);

    const options = {
      method: methodUpper,
      headers,
    };

    if (hasBody) {
      options.body = typeof data === "string" ? data : JSON.stringify(data);
    }

    const run = () =>
      fetch(fullUrl, options).then((res) => {
        // Still parse JSON on non-OK so existing callers that check res.code keep working.
        return res.json().catch(() => {
          throw new TypeError("Failed to fetch");
        });
      });

    if (opts.skipQueue) {
      return run();
    }

    // Default priority: GET list/detail = 0; allow callers to pass higher for above-the-fold.
    return enqueueRequest(run, { priority: opts.priority ?? 0 });
  }

  get(url, opts) {
    return this.request(url, "GET", null, opts).catch((err) => err);
  }

  post(url, data, opts) {
    return this.request(url, "POST", data, opts).catch((err) => err);
  }

  put(url, data, opts) {
    return this.request(url, "PUT", data, opts).catch((err) => err);
  }

  delete(url, opts) {
    return this.request(url, "DELETE", null, opts).catch((err) => err);
  }

  patch(url, data, opts) {
    return this.request(url, "PATCH", data, opts).catch((err) => err);
  }

  postForm(url, formData, opts = {}) {
    const token = storage.getItem("access_token");
    const fullUrl = this.joinURL(this.domain, url);
    const run = () =>
      fetch(fullUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      }).then((res) => res.json());

    if (opts.skipQueue) return run();
    return enqueueRequest(run, { priority: opts.priority ?? 0 });
  }
}

export default Service;
